/**
 * Vue admin : détail par module — voir qui a REELLEMENT regardé quoi.
 * Distingue les cas ambigus (ex : 90% vu mais non validé) et propose :
 *  - Filtres : "Presque fini", "Jamais commencé", "Bloqué X jours"
 *  - Bouton "Forcer comme vu" par utilisateur/contenu
 *  - Bouton "Récupération douce" (batch) au niveau du module
 */

import { useEffect, useState, useMemo } from 'react';
import { academyAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
    Users, PlayCircle, CheckCircle2, AlertTriangle, Search, RefreshCw, ChevronDown, ChevronUp,
    Sparkles, Clock, Video, FileText, BookOpen, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const TYPE_ICON = { video: Video, document: FileText, article: BookOpen };

const SIGNAL_META = {
    viewed: { label: 'Vu', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    almost_finished: { label: 'Presque fini', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    started: { label: 'Commencé', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    opened: { label: 'Ouvert', color: 'bg-slate-100 text-slate-600 border-slate-200' },
    never: { label: 'Jamais', color: 'bg-rose-50 text-rose-600 border-rose-200' },
};

function fmtSec(sec) {
    if (!sec) return '—';
    sec = Math.round(sec);
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    if (h) return `${h}h${String(m).padStart(2, '0')}`;
    if (m) return `${m}min${s ? String(s).padStart(2, '0') : ''}`;
    return `${s}s`;
}

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch { return iso; }
}

function ContentBadge({ c, onForceViewed, canForce }) {
    const Icon = TYPE_ICON[c.type] || FileText;
    const meta = SIGNAL_META[c.signal] || SIGNAL_META.never;
    return (
        <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1', meta.color)} data-testid={`content-badge-${c.content_id}`}>
            <Icon className="w-3 h-3 shrink-0" />
            <span className="text-[11px] font-medium truncate max-w-[140px]" title={c.title}>{c.title}</span>
            <span className="text-[10px] opacity-80">
                {c.max_watched_percent != null ? `${Math.round(c.max_watched_percent)}%` : '—'}
                {c.sessions_count ? ` · ${c.sessions_count}s` : ''}
            </span>
            {c.viewed && c.viewed_source && (
                <span className="text-[9px] italic opacity-70">
                    {c.viewed_source === 'manual' ? 'clic' :
                        c.viewed_source === 'auto_video' ? 'auto' :
                            c.viewed_source === 'admin_forced' ? 'admin' :
                                c.viewed_source === 'auto_recovered' ? 'récup.' : c.viewed_source}
                </span>
            )}
            {!c.viewed && canForce && (
                <Button
                    variant="ghost" size="sm"
                    className="h-5 px-1 text-[10px]"
                    onClick={onForceViewed}
                    data-testid={`force-viewed-btn-${c.content_id}`}
                    title="Forcer comme vu"
                >
                    <CheckCircle2 className="w-3 h-3" />
                </Button>
            )}
        </div>
    );
}

function UserRow({ row, onForceViewed, canForce }) {
    const [expanded, setExpanded] = useState(false);
    const u = row.user;
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
    const counts = row.counts;
    const pct = counts.total ? Math.round((counts.viewed / counts.total) * 100) : 0;
    const almost = counts.almost_finished;

    return (
        <>
            <tr
                className="hover:bg-slate-50 cursor-pointer border-b border-slate-100"
                onClick={() => setExpanded(v => !v)}
                data-testid={`insight-user-row-${u.id}`}
            >
                <td className="p-3">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[11px] font-bold">
                            {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-800">{name}</p>
                            <p className="text-[11px] text-slate-500">{u.email}</p>
                        </div>
                    </div>
                </td>
                <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                        <span className="text-sm font-semibold">{counts.viewed}/{counts.total}</span>
                        <Progress value={pct} className="h-1.5 w-14" />
                    </div>
                </td>
                <td className="p-3 text-center">
                    {almost > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100" data-testid={`almost-badge-${u.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {almost}
                        </Badge>
                    ) : <span className="text-xs text-slate-400">0</span>}
                </td>
                <td className="p-3 text-center">
                    <span className={cn('text-xs font-semibold',
                        row.quiz_passed ? 'text-emerald-600' :
                            row.quiz_score != null ? 'text-rose-500' : 'text-slate-400')}>
                        {row.quiz_score != null ? `${row.quiz_score}%` : '—'}
                    </span>
                </td>
                <td className="p-3 text-center text-xs text-slate-600">{row.total_time_label}</td>
                <td className="p-3 text-center text-xs text-slate-500">
                    {row.completed_at ? <Badge variant="outline" className="text-emerald-600 border-emerald-300">Terminé</Badge>
                        : row.started_at ? <span>Depuis {fmtDate(row.started_at)}</span>
                            : <span className="text-slate-400">Jamais</span>}
                </td>
                <td className="p-3 text-center w-8">
                    {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={7} className="bg-slate-50/50 p-3">
                        <div className="flex flex-wrap gap-2" data-testid={`insight-user-contents-${u.id}`}>
                            {row.contents.map(c => (
                                <ContentBadge
                                    key={c.content_id}
                                    c={c}
                                    canForce={canForce}
                                    onForceViewed={() => onForceViewed(u, c)}
                                />
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function AcademyVideoInsights({ modules }) {
    const [selectedModuleId, setSelectedModuleId] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // all | almost | never | not_completed | completed
    const [recoverOpen, setRecoverOpen] = useState(false);
    const [recoverRunning, setRecoverRunning] = useState(false);
    const [recoverDryRun, setRecoverDryRun] = useState(null);

    useEffect(() => {
        if (!selectedModuleId) { setData(null); return; }
        loadModule();
    }, [selectedModuleId]);

    const loadModule = async () => {
        setLoading(true);
        try {
            const res = await academyAPI.getModuleUsersProgress(selectedModuleId);
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de chargement');
        } finally { setLoading(false); }
    };

    const handleForceViewed = async (user, content) => {
        if (!selectedModuleId) return;
        if (!window.confirm(`Forcer « ${content.title} » comme vu pour ${user.first_name} ${user.last_name} ?`)) return;
        try {
            await academyAPI.forceViewed({
                user_id: user.id,
                content_id: content.content_id,
                module_id: selectedModuleId,
            });
            toast.success('Contenu marqué comme vu');
            loadModule();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const runRecoverDryRun = async () => {
        if (!selectedModuleId) return;
        setRecoverRunning(true);
        try {
            const res = await academyAPI.recoverVideoProgress({
                dry_run: true,
                module_id: selectedModuleId,
                min_max_watched_percent: 60,
                min_time_seconds: 120,
            });
            setRecoverDryRun(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setRecoverRunning(false); }
    };

    const runRecoverApply = async () => {
        if (!selectedModuleId) return;
        setRecoverRunning(true);
        try {
            const res = await academyAPI.recoverVideoProgress({
                dry_run: false,
                module_id: selectedModuleId,
                min_max_watched_percent: 60,
                min_time_seconds: 120,
            });
            toast.success(`${res.data.contents_recovered} contenus récupérés sur ${res.data.users_impacted} utilisateurs`);
            setRecoverOpen(false);
            setRecoverDryRun(null);
            loadModule();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setRecoverRunning(false); }
    };

    const filteredRows = useMemo(() => {
        if (!data?.users) return [];
        return data.users.filter(row => {
            // Filtre catégorie
            if (filter === 'almost' && row.counts.almost_finished === 0) return false;
            if (filter === 'never' && row.counts.viewed !== 0) return false;
            if (filter === 'not_completed' && row.completed_at) return false;
            if (filter === 'completed' && !row.completed_at) return false;
            // Recherche texte
            if (search) {
                const q = search.toLowerCase();
                const name = `${row.user.first_name || ''} ${row.user.last_name || ''} ${row.user.email || ''}`.toLowerCase();
                if (!name.includes(q)) return false;
            }
            return true;
        });
    }, [data, filter, search]);

    const counts = useMemo(() => {
        if (!data?.users) return { all: 0, almost: 0, never: 0, not_completed: 0, completed: 0 };
        return {
            all: data.users.length,
            almost: data.users.filter(u => u.counts.almost_finished > 0).length,
            never: data.users.filter(u => u.counts.viewed === 0 && !u.started_at).length,
            not_completed: data.users.filter(u => !u.completed_at).length,
            completed: data.users.filter(u => !!u.completed_at).length,
        };
    }, [data]);

    return (
        <Card className="border-l-4 border-l-emerald-500" data-testid="academy-video-insights">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <PlayCircle className="w-5 h-5 text-emerald-600" />
                        Suivi vidéo par module
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Select value={selectedModuleId} onValueChange={setSelectedModuleId}>
                            <SelectTrigger className="w-[320px]" data-testid="module-select">
                                <SelectValue placeholder="Sélectionne un module…" />
                            </SelectTrigger>
                            <SelectContent>
                                {(modules || []).map(m => (
                                    <SelectItem key={m.id} value={m.id} data-testid={`module-opt-${m.id}`}>
                                        {m.title} {m.is_published ? '' : '(brouillon)'}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {selectedModuleId && (
                            <Button size="sm" variant="outline" onClick={() => setRecoverOpen(true)} data-testid="btn-open-recover">
                                <Sparkles className="w-4 h-4 mr-1" /> Récupération auto
                            </Button>
                        )}
                    </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                    Voir <b>ce que chacun a réellement regardé</b>, distinguer les vidéos « presque finies » des vraies non-vues,
                    et rattraper les cas où l&apos;utilisateur a bien vu mais n&apos;a pas été crédité.
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                {!selectedModuleId && (
                    <div className="text-sm text-slate-500 py-6 text-center">
                        Sélectionne un module pour voir le détail par utilisateur.
                    </div>
                )}
                {selectedModuleId && loading && (
                    <div className="py-8 text-center text-slate-400 text-sm">Chargement…</div>
                )}
                {selectedModuleId && data && !loading && (
                    <>
                        {/* Filtres */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {[
                                { k: 'all', label: `Tous (${counts.all})` },
                                { k: 'almost', label: `Presque fini (${counts.almost})`, icon: AlertTriangle, color: 'text-amber-600' },
                                { k: 'not_completed', label: `Non terminés (${counts.not_completed})` },
                                { k: 'never', label: `Jamais commencé (${counts.never})` },
                                { k: 'completed', label: `Terminés (${counts.completed})`, icon: CheckCircle2, color: 'text-emerald-600' },
                            ].map(f => (
                                <Button
                                    key={f.k}
                                    variant={filter === f.k ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilter(f.k)}
                                    data-testid={`filter-${f.k}`}
                                    className="h-8"
                                >
                                    {f.icon && <f.icon className={cn('w-3 h-3 mr-1', filter === f.k ? '' : f.color)} />}
                                    {f.label}
                                </Button>
                            ))}
                            <div className="relative w-64 ml-auto">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Rechercher un utilisateur…"
                                    className="pl-9 h-9"
                                    data-testid="search-insight-user"
                                />
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                        <th className="p-3 text-left">Utilisateur</th>
                                        <th className="p-3 text-center">Vus / Total</th>
                                        <th className="p-3 text-center">Presque fini</th>
                                        <th className="p-3 text-center">Quiz</th>
                                        <th className="p-3 text-center">Temps</th>
                                        <th className="p-3 text-center">Statut</th>
                                        <th className="p-3 text-center w-8"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="p-8 text-center text-slate-400 text-sm">
                                                Aucun utilisateur dans ce filtre.
                                            </td>
                                        </tr>
                                    ) : filteredRows.map(row => (
                                        <UserRow
                                            key={row.user.id}
                                            row={row}
                                            canForce={true}
                                            onForceViewed={handleForceViewed}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="text-[11px] text-slate-500 space-y-1">
                            <p className="flex items-center gap-1"><Users className="w-3 h-3" /> Clique sur une ligne pour voir le détail par vidéo. Chaque badge indique le <b>% max regardé</b> et le nb de sessions.</p>
                            <p className="flex items-center gap-1"><Clock className="w-3 h-3" /> Un badge <span className="text-amber-700">« Presque fini »</span> = vidéo regardée à ≥ 80% mais non validée : idéal pour indulgence.</p>
                        </div>
                    </>
                )}
            </CardContent>

            {/* Dialog Récupération douce */}
            <Dialog open={recoverOpen} onOpenChange={(o) => { setRecoverOpen(o); if (!o) setRecoverDryRun(null); }}>
                <DialogContent className="max-w-md" data-testid="recover-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-indigo-600" /> Récupération douce
                        </DialogTitle>
                        <DialogDescription>
                            Marque comme « vu » toute vidéo non validée qui remplit AU MOINS UN critère :
                            <br />• % max regardé ≥ <b>60%</b>
                            <br />• OU temps actif ≥ <b>2 minutes</b> (et vidéo ouverte)
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {!recoverDryRun ? (
                            <div className="text-sm text-slate-600">
                                Étape 1 : <b>simulation</b> pour compter combien d&apos;utilisateurs et contenus seraient impactés.
                            </div>
                        ) : (
                            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-sm">
                                <p className="font-semibold text-indigo-800">Simulation terminée :</p>
                                <p className="text-slate-700 mt-1">
                                    <b>{recoverDryRun.users_impacted}</b> utilisateurs impactés · <b>{recoverDryRun.contents_recovered}</b> contenus seraient marqués vus.
                                </p>
                                <p className="text-xs text-slate-500 mt-2">Vérifie et confirme pour appliquer.</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        {!recoverDryRun ? (
                            <>
                                <Button variant="outline" onClick={() => setRecoverOpen(false)}>Annuler</Button>
                                <Button onClick={runRecoverDryRun} disabled={recoverRunning} data-testid="btn-recover-dry-run">
                                    <RefreshCw className={cn('w-4 h-4 mr-1', recoverRunning && 'animate-spin')} />
                                    {recoverRunning ? 'Simulation…' : 'Simuler'}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setRecoverDryRun(null)}>Retour</Button>
                                <Button onClick={runRecoverApply} disabled={recoverRunning || recoverDryRun.contents_recovered === 0} data-testid="btn-recover-apply">
                                    <ShieldCheck className="w-4 h-4 mr-1" />
                                    {recoverRunning ? 'Application…' : `Appliquer (${recoverDryRun.contents_recovered})`}
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
